import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MsalService } from '@azure/msal-angular';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { ProgressBarModule } from 'primeng/progressbar';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { catchError, throwError } from 'rxjs';
import { BulkMemberUpdate } from '../../../model/bulk/BulkMemberUpdate';
import { BulkUpdateResponse } from '../../../model/bulk/BulkUpdateResponse';
import { BulkUpdateJobResponse, JobStatus } from '../../../model/bulk/JobStatus';
import { HttpResponse } from '../../../model/HttpResponse';
import { LoadingService } from '../../../services/loading.service';
import { MemberService } from '../../../services/member.service';

type BulkOperationMode = 'deactivate' | 'reactivate-schools';

@Component({
  selector: 'app-bulk-members',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    DropdownModule,
    InputTextModule,
    DialogModule,
    MessagesModule,
    ToastModule,
    ProgressBarModule,
    TabViewModule
  ],
  providers: [MessageService],
  templateUrl: './bulk-members.component.html',
  styleUrls: ['./bulk-members.component.scss']
})
export class BulkMembersComponent implements OnInit, OnDestroy {
  // Operación actual en curso (la que disparó los modales)
  currentOperation: BulkOperationMode = 'deactivate';

  // Propiedades de confirmación
  confirmationText: string = '';
  requiredConfirmationText: string = 'DESACTIVAR TODOS';
  yearFilter?: number;
  reactivateYearFilter?: number;
  showConfirmModal: boolean = false;
  showSecondConfirmModal: boolean = false;

  // Lista de años
  yearsList: number[] = [];

  // Propiedades para polling y progreso
  currentJobId: string | null = null;
  pollingInterval: any = null;
  jobStatus: JobStatus | null = null;
  showProgressDialog: boolean = false;

  constructor(
    private memberService: MemberService,
    private loadingService: LoadingService,
    private messageService: MessageService,
    public authService: MsalService
  ) {}

  ngOnInit(): void {
    this.getYearsList();
    this.yearFilter = moment().year();
    this.reactivateYearFilter = moment().year();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /**
   * Genera la lista de años disponibles para filtrar
   */
  getYearsList(): void {
    const actualYear = moment().year();
    for (let index = 0; index < 50; index++) {
      const year = 2025 + index;
      if (year >= 2025 && year <= actualYear) {
        this.yearsList.push(year);
      }
    }
  }

  /**
   * Obtiene el email del usuario autenticado
   */
  getUserEmail(): string {
    const account = this.authService.instance.getActiveAccount();
    return account?.username || '';
  }

  /**
   * Muestra el modal de confirmación inicial para desactivación masiva
   */
  showDeactivateAllModal(): void {
    this.currentOperation = 'deactivate';
    this.requiredConfirmationText = 'DESACTIVAR TODOS';
    this.confirmationText = '';
    this.showConfirmModal = true;
  }

  /**
   * Muestra el modal de confirmación inicial para reactivación de escuelas
   */
  showReactivateSchoolsModal(): void {
    this.currentOperation = 'reactivate-schools';
    this.requiredConfirmationText = 'REACTIVAR ESCUELAS';
    this.confirmationText = '';
    this.showConfirmModal = true;
  }

  /**
   * Indica si la operación actual es reactivación de escuelas
   */
  isReactivateOperation(): boolean {
    return this.currentOperation === 'reactivate-schools';
  }

  /**
   * Año aplicable a la operación actual
   */
  getCurrentYearFilter(): number | undefined {
    return this.isReactivateOperation() ? this.reactivateYearFilter : this.yearFilter;
  }

  /**
   * Cancela la operación y cierra el modal
   */
  cancelOperation(): void {
    this.showConfirmModal = false;
    this.showSecondConfirmModal = false;
    this.confirmationText = '';
  }

  /**
   * Valida el texto de confirmación y muestra segundo modal
   */
  confirmDeactivateAll(): void {
    if (this.confirmationText.trim() !== this.requiredConfirmationText) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Confirmación Incorrecta',
        detail: `Debes escribir exactamente "${this.requiredConfirmationText}" para continuar`
      });
      return;
    }

    // Mostrar segunda confirmación
    this.showConfirmModal = false;
    this.showSecondConfirmModal = true;
  }

  /**
   * Ejecuta la operación masiva (desactivación o reactivación de escuelas)
   */
  executeBulkOperation(): void {
    this.loadingService.showLoading();
    this.showSecondConfirmModal = false;

    const bulkUpdate: BulkMemberUpdate = this.isReactivateOperation()
      ? {
          isActive: true,
          year: this.reactivateYearFilter,
          modifiedBy: this.getUserEmail(),
          nivel: 'ESCUELA',
          memberType: 'EXTERNAL'
        }
      : {
          isActive: false,
          year: this.yearFilter,
          modifiedBy: this.getUserEmail()
        };

    this.memberService
      .bulkUpdateMembers(bulkUpdate)
      .pipe(catchError(this.handleError.bind(this)))
      .subscribe((res: HttpResponse) => {
        this.loadingService.hideLoading();

        if (res.code === 200) {
          const jobResponse = res.response as BulkUpdateJobResponse;
          this.currentJobId = jobResponse.jobId;

          this.messageService.add({
            severity: 'info',
            summary: 'Proceso Iniciado',
            detail: jobResponse.message,
            life: 3000
          });

          // Mostrar diálogo de progreso y comenzar polling
          this.showProgressDialog = true;
          this.jobStatus = null;
          this.startPolling();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo iniciar la operación masiva'
          });
        }
      });
  }

  /**
   * Limpia el formulario
   */
  resetForm(): void {
    this.confirmationText = '';
    this.yearFilter = moment().year();
    this.reactivateYearFilter = moment().year();
  }

  /**
   * Verifica si el botón de confirmación debe estar habilitado
   */
  isConfirmButtonDisabled(): boolean {
    return this.confirmationText.trim() !== this.requiredConfirmationText;
  }

  /**
   * Obtiene la fecha y hora actual formateada
   */
  getCurrentDateTime(): string {
    return moment().format('DD/MM/YYYY HH:mm:ss');
  }

  /**
   * Manejo de errores HTTP
   */
  private handleError(error: HttpErrorResponse) {
    this.loadingService.hideLoading();
    this.showConfirmModal = false;
    this.showSecondConfirmModal = false;

    if (error.status === 422) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos no válidos',
        detail: 'Los datos enviados no son válidos. Por favor verifica e inténtalo nuevamente.'
      });
      console.error('Validation error:', error.error);
    } else if (error.status === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de Conexión',
        detail: 'No se pudo conectar con el servidor. Por favor verifica tu conexión e inténtalo nuevamente.',
        life: 30000
      });
    } else if (error.status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: 'Sin Autorización',
        detail: 'No tienes permisos para realizar esta operación. Solo administradores SID pueden ejecutar operaciones masivas.'
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Se presentó un error ejecutando la operación. Por favor inténtalo nuevamente. Si el error persiste contacte al administrador de la aplicación.'
      });
      console.error(
        `Backend returned code ${error.status}, body was: `,
        error.error
      );
    }

    return throwError(
      () => new Error('Something bad happened; please try again later.')
    );
  }

  /**
   * Inicia el polling para consultar el estado del job
   */
  startPolling(): void {
    this.pollingInterval = setInterval(() => {
      if (this.currentJobId) {
        this.memberService.getJobStatus(this.currentJobId)
          .subscribe({
            next: (res: HttpResponse) => {
              if (res.code === 200) {
                this.jobStatus = res.response as JobStatus;

                // Si el job terminó (completado o fallido), detener polling
                if (this.jobStatus.status === 'completed' || this.jobStatus.status === 'failed') {
                  this.stopPolling();
                  this.showFinalResult();
                }
              }
            },
            error: (error) => {
              console.error('Error consultando estado del job:', error);
              // Continuar polling a pesar del error
            }
          });
      }
    }, 3000); // Consultar cada 3 segundos
  }

  /**
   * Detiene el polling
   */
  stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Muestra el resultado final de la operación
   */
  showFinalResult(): void {
    if (!this.jobStatus) return;

    this.showProgressDialog = false;

    if (this.jobStatus.status === 'completed') {
      const result = this.jobStatus.result;

      this.messageService.add({
        severity: 'success',
        summary: 'Operación Completada',
        detail: `${result.message}. ${result.details.successCount} exitosos, ${result.details.errorCount} errores.`,
        life: 10000
      });

      this.resetForm();
    } else if (this.jobStatus.status === 'failed') {
      this.messageService.add({
        severity: 'error',
        summary: 'Operación Fallida',
        detail: this.jobStatus.error || 'La operación masiva falló',
        life: 10000
      });
    }

    // Limpiar estado
    this.currentJobId = null;
    this.jobStatus = null;
  }

  /**
   * Calcula el porcentaje de progreso
   */
  getProgressPercentage(): number {
    if (!this.jobStatus || !this.jobStatus.progress || this.jobStatus.progress.total === 0) {
      return 0;
    }
    return Math.round((this.jobStatus.progress.processed / this.jobStatus.progress.total) * 100);
  }

  /**
   * Obtiene el mensaje de estado actual
   */
  getStatusMessage(): string {
    if (!this.jobStatus) return 'Iniciando...';

    switch (this.jobStatus.status) {
      case 'pending':
        return 'Preparando operación...';
      case 'processing':
        return `Procesando: ${this.jobStatus.progress.processed} / ${this.jobStatus.progress.total}`;
      case 'completed':
        return 'Completado';
      case 'failed':
        return 'Error en la operación';
      default:
        return 'Estado desconocido';
    }
  }
}
